import React, { useState } from 'react';
import { Plus, Eye, Edit2, Trash2, Key, Lock, Unlock, Calendar, FileText } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PermissionForm from './PermissionForm';

const PermissionList = () => {
  const [view, setView] = useState('list');
  const [selectedPermission, setSelectedPermission] = useState(null);
  
  const [permissions, setPermissions] = useState([
    // User Permissions
    {
      id: 1,
      name: 'user.create',
      displayName: 'Create User',
      resourceType: 'User',
      action: 'CREATE',
      description: 'Permission to create new users in the system',
      type: 'System',
      createdAt: '2024-01-15T10:00:00'
    },
    {
      id: 2,
      name: 'user.read',
      displayName: 'View User',
      resourceType: 'User',
      action: 'READ',
      description: 'Permission to view user information',
      type: 'System',
      createdAt: '2024-01-15T10:00:00'
    },
    {
      id: 3,
      name: 'user.update',
      displayName: 'Update User',
      resourceType: 'User',
      action: 'UPDATE',
      description: 'Permission to update user information',
      type: 'System',
      createdAt: '2024-01-15T10:00:00'
    },
    {
      id: 4,
      name: 'user.delete',
      displayName: 'Delete User',
      resourceType: 'User',
      action: 'DELETE',
      description: 'Permission to delete users from the system',
      type: 'System',
      createdAt: '2024-01-15T10:00:00'
    },
    // Role Permissions
    {
      id: 5,
      name: 'role.create',
      displayName: 'Create Role',
      resourceType: 'Role',
      action: 'CREATE',
      description: 'Permission to create new roles',
      type: 'System',
      createdAt: '2024-01-15T10:00:00'
    },
    {
      id: 6,
      name: 'role.read',
      displayName: 'View Role',
      resourceType: 'Role',
      action: 'READ',
      description: 'Permission to view role information',
      type: 'System',
      createdAt: '2024-01-15T10:00:00'
    },
    {
      id: 7,
      name: 'role.update',
      displayName: 'Update Role',
      resourceType: 'Role',
      action: 'UPDATE',
      description: 'Permission to update role information',
      type: 'System',
      createdAt: '2024-01-15T10:00:00'
    },
    {
      id: 8,
      name: 'role.delete',
      displayName: 'Delete Role',
      resourceType: 'Role',
      action: 'DELETE',
      description: 'Permission to delete roles',
      type: 'System',
      createdAt: '2024-01-15T10:00:00'
    },
    // Project Permissions
    {
      id: 9,
      name: 'project.create',
      displayName: 'Create Project',
      resourceType: 'Project',
      action: 'CREATE',
      description: 'Permission to create new projects',
      type: 'Business',
      createdAt: '2024-02-01T10:00:00'
    },
    {
      id: 10,
      name: 'project.read',
      displayName: 'View Project',
      resourceType: 'Project',
      action: 'READ',
      description: 'Permission to view project information',
      type: 'Business',
      createdAt: '2024-02-01T10:00:00'
    },
    {
      id: 11,
      name: 'project.update',
      displayName: 'Update Project',
      resourceType: 'Project',
      action: 'UPDATE',
      description: 'Permission to update project information',
      type: 'Business',
      createdAt: '2024-02-01T10:00:00'
    },
    {
      id: 12,
      name: 'project.delete',
      displayName: 'Delete Project',
      resourceType: 'Project',
      action: 'DELETE',
      description: 'Permission to delete projects',
      type: 'Business',
      createdAt: '2024-02-01T10:00:00'
    },
    // Budget Permissions
    {
      id: 13,
      name: 'budget.create',
      displayName: 'Create Budget',
      resourceType: 'Budget',
      action: 'CREATE',
      description: 'Permission to create budget entries',
      type: 'Business',
      createdAt: '2024-02-01T10:00:00'
    },
    {
      id: 14,
      name: 'budget.read',
      displayName: 'View Budget',
      resourceType: 'Budget',
      action: 'READ',
      description: 'Permission to view budget information',
      type: 'Business',
      createdAt: '2024-02-01T10:00:00'
    },
    {
      id: 15,
      name: 'budget.update',
      displayName: 'Update Budget',
      resourceType: 'Budget',
      action: 'UPDATE',
      description: 'Permission to update budget entries',
      type: 'Business',
      createdAt: '2024-02-01T10:00:00'
    },
    {
      id: 16,
      name: 'budget.delete',
      displayName: 'Delete Budget',
      resourceType: 'Budget',
      action: 'DELETE',
      description: 'Permission to delete budget entries',
      type: 'Business',
      createdAt: '2024-02-01T10:00:00'
    },
    // Report Permissions
    {
      id: 17,
      name: 'report.generate',
      displayName: 'Generate Report',
      resourceType: 'Report',
      action: 'GENERATE',
      description: 'Permission to generate reports',
      type: 'Business',
      createdAt: '2024-02-15T10:00:00'
    },
    {
      id: 18,
      name: 'report.export',
      displayName: 'Export Report',
      resourceType: 'Report',
      action: 'EXPORT',
      description: 'Permission to export reports to various formats',
      type: 'Business',
      createdAt: '2024-02-15T10:00:00'
    }
  ]);

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'name', direction: 'ASC' });
  const [searchValue, setSearchValue] = useState('');
  const [filterResource, setFilterResource] = useState('ALL');
  const [filterAction, setFilterAction] = useState('ALL');
  const [filterType, setFilterType] = useState('ALL');

  // Filter permissions based on search and filters
  const filteredPermissions = permissions.filter(permission => {
    const matchesSearch = 
      permission.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      permission.displayName.toLowerCase().includes(searchValue.toLowerCase()) ||
      permission.description.toLowerCase().includes(searchValue.toLowerCase());
    const matchesResource = filterResource === 'ALL' || permission.resourceType === filterResource;
    const matchesAction = filterAction === 'ALL' || permission.action === filterAction;
    const matchesType = filterType === 'ALL' || permission.type === filterType;
    return matchesSearch && matchesResource && matchesAction && matchesType;
  });

  // Sort permissions
  const sortedPermissions = [...filteredPermissions].sort((a, b) => {
    const aValue = a[sortConfig.sortBy];
    const bValue = b[sortConfig.sortBy];
    
    if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
    return 0;
  });

  // Paginate permissions
  const startIndex = currentPage * pageSize;
  const paginatedPermissions = sortedPermissions.slice(startIndex, startIndex + pageSize);

  // Get unique values for filters
  const uniqueResources = [...new Set(permissions.map(p => p.resourceType))].sort();
  const uniqueActions = [...new Set(permissions.map(p => p.action))].sort();
  const uniqueTypes = [...new Set(permissions.map(p => p.type))].sort();

  // Format datetime
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('id-ID', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  // Get action color
  const getActionColor = (action) => {
    const colors = {
      'CREATE': 'bg-green-100 text-green-800',
      'READ': 'bg-blue-100 text-blue-800',
      'UPDATE': 'bg-yellow-100 text-yellow-800',
      'DELETE': 'bg-red-100 text-red-800',
      'GENERATE': 'bg-purple-100 text-purple-800',
      'EXPORT': 'bg-indigo-100 text-indigo-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {startIndex + index + 1}
        </span>
      )
    },
    {
      key: 'name',
      label: 'Permission Name',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <Key className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">{row.displayName}</div>
            <div className="text-xs text-gray-500 font-mono">{row.name}</div>
          </div>
        </div>
      )
    },
    {
      key: 'resourceType',
      label: 'Resource Type',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-900">{row.resourceType}</span>
        </div>
      )
    },
    {
      key: 'action',
      label: 'Action',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(row.action)}`}>
          {row.action}
        </span>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <span className="text-sm text-gray-700">{row.description}</span>
      )
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          row.type === 'System' 
            ? 'bg-purple-100 text-purple-800' 
            : 'bg-orange-100 text-orange-800'
        }`}>
          {row.type === 'System' ? <Lock className="w-3 h-3 mr-1" /> : <Unlock className="w-3 h-3 mr-1" />}
          {row.type}
        </span>
      )
    },
    {
      key: 'createdAt',
      label: 'Created At',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-700">{formatDateTime(row.createdAt)}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button 
            onClick={() => handleView(row)} 
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button 
            onClick={() => handleEdit(row)} 
            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
            disabled={row.type === 'System'}
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button 
            onClick={() => handleDelete(row)} 
            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
            disabled={row.type === 'System'}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  // Handlers
  const handleAddNew = () => {
    setView('add');
    setSelectedPermission(null);
  };

  const handleView = (permission) => {
    setSelectedPermission(permission);
    setView('detail');
  };

  const handleEdit = (permission) => {
    if (permission.type === 'System') {
      alert('Cannot edit system permission!');
      return;
    }
    setSelectedPermission(permission);
    setView('edit');
  };

  const handleDelete = (permission) => {
    if (permission.type === 'System') {
      alert('Cannot delete system permission!');
      return;
    }
    if (window.confirm(`Are you sure you want to delete permission "${permission.displayName}"?`)) {
      setPermissions(prev => prev.filter(p => p.id !== permission.id));
      alert('Permission deleted successfully!');
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedPermission(null);
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  // Render Form View
  if (view === 'add' || view === 'edit') {
    return <PermissionForm onBack={handleBackToList} permissionData={selectedPermission} mode={view} />;
  }

  // Render Detail View
  if (view === 'detail' && selectedPermission) {
    return <PermissionDetail permission={selectedPermission} onBack={handleBackToList} onEdit={() => handleEdit(selectedPermission)} />;
  }

  // Render List View (Default)
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Data Permission</h1>
            <p className="text-gray-600 mt-1">Halaman untuk mengelola Permission</p>
          </div>
          <button 
            onClick={handleAddNew} 
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Permission
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Permissions</p>
              <p className="text-3xl font-bold mt-2">{permissions.length}</p>
              <p className="text-blue-100 text-sm mt-2">All permissions</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Key className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">System</p>
              <p className="text-3xl font-bold mt-2">{permissions.filter(p => p.type === 'System').length}</p>
              <p className="text-purple-100 text-sm mt-2">System permissions</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Lock className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-600 to-orange-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Business</p>
              <p className="text-3xl font-bold mt-2">{permissions.filter(p => p.type === 'Business').length}</p>
              <p className="text-orange-100 text-sm mt-2">Business permissions</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Unlock className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Resources</p>
              <p className="text-3xl font-bold mt-2">{uniqueResources.length}</p>
              <p className="text-green-100 text-sm mt-2">Resource types</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="space-y-4">
          {/* Resource Type Filter */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-gray-700 w-32">Resource Type:</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterResource('ALL')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterResource === 'ALL'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({permissions.length})
              </button>
              {uniqueResources.map(resource => (
                <button
                  key={resource}
                  onClick={() => setFilterResource(resource)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterResource === resource
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {resource} ({permissions.filter(p => p.resourceType === resource).length})
                </button>
              ))}
            </div>
          </div>

          {/* Action Filter */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-gray-700 w-32">Action:</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterAction('ALL')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterAction === 'ALL'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({permissions.length})
              </button>
              {uniqueActions.map(action => (
                <button
                  key={action}
                  onClick={() => setFilterAction(action)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterAction === action
                      ? 'bg-green-600 text-white'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {action} ({permissions.filter(p => p.action === action).length})
                </button>
              ))}
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-gray-700 w-32">Type:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('ALL')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterType === 'ALL'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({permissions.length})
              </button>
              {uniqueTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterType === type
                      ? (type === 'System' ? 'bg-purple-600 text-white' : 'bg-orange-600 text-white')
                      : (type === 'System' ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-orange-100 text-orange-700 hover:bg-orange-200')
                  }`}
                >
                  {type} ({permissions.filter(p => p.type === type).length})
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* DataTable Component */}
      <DataTable
        columns={columns}
        data={paginatedPermissions}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={filteredPermissions.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortConfig={sortConfig}
        isLoading={false}
        onSearch={handleSearch}
        searchValue={searchValue}
        searchPlaceholder="Search by permission name or description..."
        enableQuickJump={true}
        customPageSizes={[5, 10, 25, 50]}
        striped={false}
        headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
        headerTextColor="text-white"
        hoverColor="hover:bg-red-50"
      />
    </div>
  );
};

// Permission Detail Component
const PermissionDetail = ({ permission, onBack, onEdit }) => {
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('id-ID', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Permission Details</h1>
              <p className="text-gray-600 mt-1">{permission.name}</p>
            </div>
          </div>
          <button 
            onClick={onEdit} 
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            disabled={permission.type === 'System'}
          >
            <Edit2 className="w-5 h-5" />
            Edit Permission
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="block text-sm font-semibold text-gray-500 mb-2">Permission Name</label>
            <p className="text-lg font-semibold text-gray-900">{permission.displayName}</p>
            <p className="text-sm text-gray-500 font-mono mt-1">{permission.name}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="block text-sm font-semibold text-gray-500 mb-2">Resource Type</label>
            <p className="text-lg font-medium text-gray-900">{permission.resourceType}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="block text-sm font-semibold text-gray-500 mb-2">Action</label>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
              {permission.action}
            </span>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="block text-sm font-semibold text-gray-500 mb-2">Type</label>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
              permission.type === 'System' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
            }`}>
              {permission.type}
            </span>
          </div>
          <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="block text-sm font-semibold text-gray-500 mb-2">Description</label>
            <p className="text-base text-gray-900">{permission.description}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="block text-sm font-semibold text-gray-500 mb-2">Created At</label>
            <p className="text-base text-gray-900">{formatDateTime(permission.createdAt)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionList;